import { useState, useEffect, useCallback } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Search, Calendar as CalIcon, CheckSquare, MapPin } from "lucide-react";
import { useUI } from "@/context/UIContext";
import { useApp } from "@/context/AppContext";
import api from "@/lib/api";
import { minutesToLabel } from "@/lib/timeUtils";
import { useNavigate } from "react-router-dom";

export function SearchDialog() {
  const { searchOpen, setSearchOpen, openEventModal } = useUI();
  const { settings } = useApp();
  const [q, setQ] = useState("");
  const [res, setRes] = useState({ events: [], tasks: [] });
  const navigate = useNavigate();
  const format = settings?.time_format || "12";

  useEffect(() => {
    if (!searchOpen) { setQ(""); setRes({ events: [], tasks: [] }); }
  }, [searchOpen]);

  useEffect(() => {
    if (!q.trim()) { setRes({ events: [], tasks: [] }); return; }
    const t = setTimeout(async () => {
      try { const r = await api.get(`/search?q=${encodeURIComponent(q)}`); setRes(r.data); } catch {}
    }, 200);
    return () => clearTimeout(t);
  }, [q]);

  const openEvent = (ev) => { setSearchOpen(false); openEventModal(ev); };
  const goTask = () => { setSearchOpen(false); navigate("/tasks"); };

  return (
    <Dialog open={searchOpen} onOpenChange={setSearchOpen}>
      <DialogContent className="max-w-xl p-0 border-0 gap-0 overflow-hidden top-[20%] translate-y-0" style={{ background: "transparent", boxShadow: "none" }}>
        <DialogTitle className="sr-only">Search</DialogTitle>
        <DialogDescription className="sr-only">Search events, tasks, locations and notes</DialogDescription>
        <div className="rounded-2xl overflow-hidden" style={{ background: "var(--bg-sidebar)", border: "1px solid var(--border-subtle)" }}>
          <div className="flex items-center gap-3 px-4 py-3.5 border-b" style={{ borderColor: "var(--border-subtle)" }}>
            <Search size={18} style={{ color: "var(--text-muted)" }} />
            <input value={q} onChange={(e) => setQ(e.target.value)} autoFocus placeholder="Search events, tasks, locations, notes..."
              className="flex-1 bg-transparent outline-none text-sm" style={{ color: "var(--text-primary)" }} data-testid="global-search-input" />
          </div>
          <div className="max-h-80 overflow-y-auto p-2">
            {res.events.length === 0 && res.tasks.length === 0 && (
              <p className="text-center text-sm py-8" style={{ color: "var(--text-muted)" }}>{q ? "No results" : "Type to search"}</p>
            )}
            {res.events.map((ev) => (
              <button key={ev.id} onClick={() => openEvent(ev)} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-white/5 text-left" data-testid="search-result-event">
                <CalIcon size={16} style={{ color: "var(--accent-primary)" }} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate" style={{ color: "var(--text-primary)" }}>{ev.title}</p>
                  <p className="text-[11px] font-mono" style={{ color: "var(--text-muted)" }}>{ev.date} · {minutesToLabel(ev.start_minutes, format)}{ev.location ? ` · ${ev.location}` : ""}</p>
                </div>
              </button>
            ))}
            {res.tasks.map((t) => (
              <button key={t.id} onClick={goTask} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-white/5 text-left">
                <CheckSquare size={16} style={{ color: "var(--accent-primary)" }} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate" style={{ color: "var(--text-primary)" }}>{t.title}</p>
                  <p className="text-[11px]" style={{ color: "var(--text-muted)" }}>Task · {t.priority}</p>
                </div>
              </button>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
